import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ScreenHeader from "./ui/ScreenHeader";
import CircleButton from "./ui/CircleButton";
import { ArrowRight, ScanBack } from "./icon";
import WishSubmitModal, { WishSubmitPhase } from "./modals/WishSubmitModal";
import { useSocket } from "../context/SocketContext";
import { buildMakeAWishStatusUpdate } from "../utils/wishSubmission";
import { useRestorableSelection } from "../utils/backNavigation";
import Male from "../assets/img/Man1.png";
import Female from "../assets/img/Woman1.png";

// Stored values are read when the wish is sent — keep "male" / "female".
const AVATARS = [
  { value: "male", label: "Male", src: Male },
  { value: "female", label: "Female", src: Female },
] as const;

/** Minimum time the "Submitting" state stays up, so it doesn't just flash. */
const SUBMITTING_MIN_MS = 1200;
/** How long the success state shows before moving on (same as social-collage). */
const SUBMITTED_HOLD_MS = 1800;

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

type AvatarChoiceScreenProps = {
  title: ReactNode;
  /** Accessible name for the option group. */
  groupLabel: string;
  /** localStorage key the choice is saved under ("gender" / "friendGender"). */
  storageKey: string;
  onBack: () => void;
  /**
   * Last step of the flow: the CTA becomes RELEASE MY WISH and opens the
   * confirm → submitting → submitted modal, which sends the wish.
   */
  isFinalStep: boolean;
  /** Not the last step: called after the choice is saved. */
  onNext?: () => void;
  /** Last step: called after the choice is saved, right before confirming. */
  onBeforeRelease?: () => void;
};

/** "Choose your (friend's) avatar" — pick a card, then NEXT / RELEASE MY WISH. */
const AvatarChoiceScreen = ({
  title,
  groupLabel,
  storageKey,
  onBack,
  isFinalStep,
  onNext,
  onBeforeRelease,
}: AvatarChoiceScreenProps) => {
  const navigate = useNavigate();
  const { socket } = useSocket();
  // Empty when arriving forward; the earlier choice when arriving via Back.
  const [selected, setSelected] = useRestorableSelection(storageKey);
  const [phase, setPhase] = useState<WishSubmitPhase>("closed");
  const [wish] = useState(() => localStorage.getItem("wish") ?? "");

  const handleCta = () => {
    if (!selected) return;
    localStorage.setItem(storageKey, selected);
    if (isFinalStep) {
      onBeforeRelease?.();
      setPhase("ready");
    } else {
      onNext?.();
    }
  };

  const releaseWish = async () => {
    const statusUpdate = buildMakeAWishStatusUpdate();
    if (!statusUpdate) {
      setPhase("closed");
      toast.error("Something is missing from your wish. Please start again.");
      return;
    }
    if (!socket) {
      setPhase("ready");
      toast.error("Can’t reach the big screen right now. Please try again.");
      return;
    }

    setPhase("submitting");
    console.log("Emitting updateMakeAWishStatus:", statusUpdate);
    socket.emit("updateMakeAWishStatus", statusUpdate);
    await sleep(SUBMITTING_MIN_MS);
    setPhase("submitted");
  };

  useEffect(() => {
    if (phase !== "submitted") return undefined;
    const timer = window.setTimeout(() => {
      setPhase("closed");
      navigate("/magic");
    }, SUBMITTED_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [phase, navigate]);

  return (
    <section className="wishScreen wishScreen--joining">
      <ScreenHeader
        leading={
          <button type="button" className="roundBack" aria-label="Back" onClick={onBack}>
            <ScanBack />
          </button>
        }
      />

      <main className="wishScreen__copy">
        <h1 className="wishScreen__title">{title}</h1>
      </main>

      <div className="joinOptions" role="radiogroup" aria-label={groupLabel}>
        {AVATARS.map(({ value, label, src }) => {
          const isSelected = selected === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`joinOption joinOption--avatar${isSelected ? " is-selected" : ""}`}
              onClick={() => setSelected(value)}
            >
              <span className="joinOption__media">
                <img src={src} alt="" />
              </span>
              <span className="joinOption__label">{label}</span>
            </button>
          );
        })}
      </div>

      {isFinalStep ? (
        <CircleButton
          className="circleButton--twoLine"
          label={
            <>
              RELEASE
              <br />
              MY WISH
            </>
          }
          ariaLabel="Release my wish"
          icon={<ArrowRight />}
          disabled={!selected}
          onClick={handleCta}
        />
      ) : (
        <CircleButton label="NEXT" icon={<ArrowRight />} disabled={!selected} onClick={handleCta} />
      )}

      <WishSubmitModal
        phase={phase}
        wish={wish}
        onSubmit={releaseWish}
        onKeepEditing={() => setPhase("closed")}
      />
    </section>
  );
};

export default AvatarChoiceScreen;
